import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Grid, Alert, CircularProgress,
  Avatar, Tooltip, IconButton, Tab, Tabs,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  TextField, MenuItem,
} from '@mui/material';
import { TrendingDown, Refresh, ExpandMore, Warning } from '@mui/icons-material';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000';

function apiFetch(path: string) {
  return fetch(`${API}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const BAND_CFG = {
  critical: { label: '🔴 Critical', color: '#b71c1c', bg: '#ffebee', pct: 75 },
  high:     { label: '🟠 High',     color: '#e65100', bg: '#fff3e0', pct: 55 },
  medium:   { label: '🟡 Medium',   color: '#f57f17', bg: '#fffde7', pct: 35 },
  low:      { label: '🟢 Low',      color: '#2e7d32', bg: '#e8f5e9', pct: 0  },
};

const SIGNAL_ICONS: Record<string, string> = {
  salary_stale:    '💰',
  perf_decline:    '📊',
  low_recognition: '🏆',
  high_absence:    '🚫',
  promo_delay:     '🚀',
  leave_exhaustion:'🏖️',
  low_engagement:  '💙',
};

function RiskGauge({ score, size = 56 }: { score: number; size?: number }) {
  const bc = score >= 75 ? '#b71c1c' : score >= 55 ? '#e65100' : score >= 35 ? '#f57f17' : '#2e7d32';
  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: bc + '20' }} />
      <Box sx={{ width: size - 8, height: size - 8, borderRadius: '50%', border: `3px solid ${bc}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" fontWeight={800} color={bc} sx={{ fontSize: size * 0.22 }}>{score}</Typography>
      </Box>
    </Box>
  );
}

function SignalBars({ signals }: { signals: any[] }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {signals.map(s => (
        <Box key={s.signal} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', width: 20 }}>{SIGNAL_ICONS[s.signal]}</Typography>
          <Box sx={{ flex: 1, minWidth: 80 }}>
            <LinearProgress variant="determinate" value={s.score}
              sx={{ height: 5, borderRadius: 2,
                '& .MuiLinearProgress-bar': {
                  bgcolor: s.score >= 70 ? '#b71c1c' : s.score >= 45 ? '#f57f17' : '#2e7d32',
                } }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28, textAlign: 'right' }}>{s.score}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function TurnoverRisk() {
  const [data, setData]       = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState(0);
  const [error, setError]     = useState('');
  const [bandFilter, setBand] = useState('');
  const [deptFilter, setDept] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { setData(await apiFetch('/api/turnover-risk')); }
    catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary ?? {};
  const all: any[]    = data?.employees ?? [];
  const deptRisk: any[] = data?.deptRisk ?? [];

  const deptsObj: Record<string, boolean> = {};
  all.forEach((e: any) => { deptsObj[e.department] = true; });
  const depts = Object.keys(deptsObj).sort();

  const filtered = all.filter(e =>
    (!bandFilter || e.band === bandFilter) &&
    (!deptFilter || e.department === deptFilter)
  );

  const criticals = all.filter(e => e.band === 'critical');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#b71c1c', width: 48, height: 48 }}><TrendingDown /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Turnover Risk Predictor</Typography>
            <Typography variant="body2" color="text.secondary">
              7-signal flight risk scoring per employee — salary, performance, recognition, absence, promotion, leave & engagement
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Re-scan"><IconButton onClick={load} disabled={loading}><Refresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {criticals.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
          <strong>{criticals.length} employee{criticals.length > 1 ? 's are' : ' is'} at critical flight risk</strong>:{' '}
          {criticals.slice(0, 4).map(e => e.name).join(', ')}{criticals.length > 4 ? ` +${criticals.length - 4} more` : ''}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* KPI row */}
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[
              { label: 'Total Employees', value: summary.total, color: '#37474f' },
              { label: '🔴 Critical Risk', value: summary.critical, color: '#b71c1c' },
              { label: '🟠 High Risk',     value: summary.high,     color: '#e65100' },
              { label: '🟡 Medium Risk',   value: summary.medium,   color: '#f57f17' },
              { label: '🟢 Low Risk',      value: summary.low,      color: '#2e7d32' },
              { label: 'Avg Risk Score',   value: summary.avgRisk,  color: summary.avgRisk >= 55 ? '#b71c1c' : summary.avgRisk >= 35 ? '#f57f17' : '#2e7d32' },
            ].map(k => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
                <Paper sx={{ p: 1.5, textAlign: 'center', borderTop: `3px solid ${k.color}` }}>
                  <Typography variant="h5" fontWeight={700} color={k.color}>{k.value ?? '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none' } }}>
            <Tab label={`Employee Risk (${all.length})`} />
            <Tab label="By Department" />
            <Tab label="Signal Breakdown" />
          </Tabs>

          {tab === 0 && (
            <>
              {/* Filters */}
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <TextField select size="small" label="Risk Band" value={bandFilter}
                  onChange={e => setBand(e.target.value)} sx={{ minWidth: 130 }}>
                  <MenuItem value="">All Bands</MenuItem>
                  {Object.entries(BAND_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Department" value={deptFilter}
                  onChange={e => setDept(e.target.value)} sx={{ minWidth: 160 }}>
                  <MenuItem value="">All Departments</MenuItem>
                  {depts.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {filtered.map(emp => {
                  const bc = BAND_CFG[emp.band as keyof typeof BAND_CFG];
                  return (
                    <Accordion key={emp.employeeId}
                      sx={{ border: `1px solid ${bc.color}30`, '&:before': { display: 'none' } }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, pr: 1 }}>
                          <RiskGauge score={emp.overallRisk} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle2" fontWeight={700}>{emp.name}</Typography>
                              <Chip size="small" label={bc.label}
                                sx={{ bgcolor: bc.bg, color: bc.color, fontWeight: 700, fontSize: '0.6rem' }} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              {emp.position && `${emp.position} · `}{emp.department} · {emp.tenureMonths}mo tenure
                            </Typography>
                            {emp.topReason && (
                              <Typography variant="caption" display="block" color={bc.color}>
                                Top risk factor: {SIGNAL_ICONS[emp.signals?.find((s: any) => s.label === emp.topReason)?.signal ?? ''] ?? ''} {emp.topReason}
                              </Typography>
                            )}
                          </Box>
                          {/* Mini signal preview */}
                          <Box sx={{ display: 'none', '@media (min-width:600px)': { display: 'block' }, minWidth: 140 }}>
                            <SignalBars signals={emp.signals ?? []} />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Grid container spacing={1}>
                          {(emp.signals ?? []).map((s: any) => {
                            const barColor = s.score >= 70 ? '#b71c1c' : s.score >= 45 ? '#f57f17' : '#2e7d32';
                            return (
                              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.signal}>
                                <Box sx={{ p: 1.25, borderRadius: 1, bgcolor: '#fafafa' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.9rem' }}>{SIGNAL_ICONS[s.signal]}</Typography>
                                      <Typography variant="caption" fontWeight={600}>{s.label}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip size="small" label={`Risk ${s.score}`}
                                        sx={{ bgcolor: barColor + '18', color: barColor, fontWeight: 700, fontSize: '0.55rem' }} />
                                      <Chip size="small" label={`${s.weight}% wt`}
                                        sx={{ bgcolor: '#f5f5f5', fontSize: '0.55rem' }} />
                                    </Box>
                                  </Box>
                                  <LinearProgress variant="determinate" value={s.score}
                                    sx={{ height: 5, borderRadius: 2, mb: 0.5,
                                      '& .MuiLinearProgress-bar': { bgcolor: barColor } }} />
                                  <Typography variant="caption" color="text.secondary">{s.interpretation}</Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                {filtered.length === 0 && (
                  <Paper sx={{ p: 3, textAlign: 'center' }}>
                    <Typography color="text.secondary">No employees match the current filters.</Typography>
                  </Paper>
                )}
              </Box>
            </>
          )}

          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Headcount</TableCell>
                    <TableCell>Avg Risk</TableCell>
                    <TableCell align="right">🔴 Critical</TableCell>
                    <TableCell>Band</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptRisk.map(d => {
                    const bc = BAND_CFG[d.band as keyof typeof BAND_CFG];
                    return (
                      <TableRow key={d.department} hover
                        sx={{ bgcolor: d.band === 'critical' ? '#fff5f5' : d.band === 'high' ? '#fffaf5' : undefined }}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{d.department}</Typography></TableCell>
                        <TableCell align="right">{d.headcount}</TableCell>
                        <TableCell sx={{ minWidth: 140 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={d.avgRisk}
                              sx={{ flex: 1, height: 8, borderRadius: 3,
                                '& .MuiLinearProgress-bar': { bgcolor: bc.color } }} />
                            <Typography variant="body2" fontWeight={700} color={bc.color}>{d.avgRisk}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {d.criticalCount > 0
                            ? <Chip size="small" label={d.criticalCount}
                                sx={{ bgcolor: '#ffebee', color: '#b71c1c', fontWeight: 700 }} />
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={bc.label}
                            sx={{ bgcolor: bc.bg, color: bc.color, fontWeight: 700, fontSize: '0.6rem' }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              {[
                { signal: 'salary_stale',    label: 'Salary Competitiveness', icon: '💰', weight: 20, desc: 'Months since last salary review. >18mo = medium risk, >24mo = high, >36mo = critical.' },
                { signal: 'perf_decline',    label: 'Performance Trajectory', icon: '📊', weight: 20, desc: 'Change in overall score between last two finalized reviews. Declining = elevated risk.' },
                { signal: 'low_recognition', label: 'Recognition (90d)',       icon: '🏆', weight: 15, desc: 'Kudos given/received in the past 90 days. Zero = 75 risk score.' },
                { signal: 'high_absence',    label: 'Unplanned Absence',       icon: '🚫', weight: 10, desc: 'Unplanned absence rate (AWOL, no-show, unplanned sick) in past 90 days.' },
                { signal: 'promo_delay',     label: 'Promotion Velocity',      icon: '🚀', weight: 15, desc: 'Months since last promotion vs dept average. >18mo behind = high risk.' },
                { signal: 'leave_exhaustion','label': 'Leave Utilisation',     icon: '🏖️', weight: 5,  desc: '>95% leave used = burnout risk indicator.' },
                { signal: 'low_engagement',  label: 'Wellness / Mood',         icon: '💙', weight: 15, desc: 'Avg mood from wellness check-ins in past 90 days. <2.5 = high risk.' },
              ].map(s => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.signal}>
                  <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: '1.5rem' }}>{s.icon}</Typography>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700}>{s.label}</Typography>
                        <Chip size="small" label={`${s.weight}% weight`}
                          sx={{ bgcolor: '#f5f5f5', fontSize: '0.6rem' }} />
                      </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary">{s.desc}</Typography>
                    {/* Show distribution */}
                    <Box sx={{ mt: 1.5 }}>
                      {['critical', 'high', 'medium', 'low'].map(b => {
                        const count = all.filter(e => {
                          const sig = e.signals?.find((x: any) => x.signal === s.signal);
                          if (!sig) return false;
                          return b === 'critical' ? sig.score >= 75
                            : b === 'high' ? sig.score >= 55 && sig.score < 75
                            : b === 'medium' ? sig.score >= 35 && sig.score < 55
                            : sig.score < 35;
                        }).length;
                        const bc2 = BAND_CFG[b as keyof typeof BAND_CFG];
                        return count > 0 ? (
                          <Box key={b} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Chip size="small" label={`${bc2.label} ×${count}`}
                              sx={{ bgcolor: bc2.bg, color: bc2.color, fontSize: '0.55rem', height: 18 }} />
                          </Box>
                        ) : null;
                      })}
                    </Box>
                  </Paper>
                </Grid>
              ))}
              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2, bgcolor: '#fafafa' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>How the Score Works</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Each of the 7 signals produces a 0–100 risk sub-score (higher = more risk). Signals are then weighted and combined: salary (20%) + performance trajectory (20%) + recognition (15%) + promotion velocity (15%) + wellness/mood (15%) + absence (10%) + leave exhaustion (5%) = overall 0–100 flight risk score.
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(BAND_CFG).map(([k, v]) => (
                      <Chip key={k} size="small" label={`${v.label} ≥${v.pct}`}
                        sx={{ bgcolor: v.bg, color: v.color, fontWeight: 700, fontSize: '0.6rem' }} />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
